import React, { useState, useEffect } from "react";
import Modal from 'react-bootstrap/Modal';
import Button from "react-bootstrap/Button";

const SelectDecorationsModal = ({ show, close, confirm, decorationsByLocation, availableLogoSets, kitId, kitName }) => {
    const apiUrl = process.env.REACT_APP_LOCALHOST_URL;
    const [selectedDecorations, setSelectedDecorations] = useState({});
    const [selectedSetId, setSelectedSetId] = useState(null);

    // Support both old isMale prop and new kitName prop
    const displayName = kitName || "New Hire Kit";
    const sets = availableLogoSets || [];

    // Initialize with first decoration from each location (individual picks)
    useEffect(() => {
        if (decorationsByLocation && decorationsByLocation.length > 0) {
            const initial = {};
            decorationsByLocation.forEach(locationGroup => {
                if (locationGroup.decorations.length > 0) {
                    initial[locationGroup.locationId] = locationGroup.decorations[0];
                }
            });
            setSelectedDecorations(initial);
        }
        setSelectedSetId(null);
    }, [decorationsByLocation, availableLogoSets]);

    const handleDecorationChange = (locationId, decoration) => {
        setSelectedSetId(null);
        setSelectedDecorations(prev => ({
            ...prev,
            [locationId]: decoration
        }));
    };

    const handleSetSelect = (setId) => {
        setSelectedSetId(setId);
    };

    const handleConfirm = () => {
        if (selectedSetId) {
            const set = sets.find(s => s.setId === selectedSetId);
            confirm(set ? set.decorations : []);
            return;
        }
        const selected = Object.values(selectedDecorations);
        confirm(selected);
    };

    const renderLogoThumb = (deco) => (
        <div key={deco.id} style={{ textAlign: "center", margin: 4 }}>
            <img
                src={apiUrl + deco.decoration_logo.imageUrl}
                height={50}
                style={{ padding: 4, backgroundColor: "#CCCCCC", borderRadius: 4 }}
                alt={deco.decoration_logo.decorationName}
            />
            <div style={{ fontSize: 10, color: "#666" }}>{deco.deco_location?.description}</div>
        </div>
    );

    return (
        <Modal dialogClassName="modal-lg" show={show} onHide={close}>
            <Modal.Header closeButton>
                <Modal.Title>
                    Select Decorations for {displayName}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p>Choose a logo set, or pick decorations individually for each location:</p>

                {sets.length > 0 && (
                    <div style={{ marginBottom: 20 }}>
                        <h5 style={{ marginBottom: 10, color: "#333" }}>Logo Sets</h5>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                            <div
                                onClick={() => handleSetSelect(null)}
                                style={{
                                    border: selectedSetId === null ? "3px solid #007bff" : "2px solid #ccc",
                                    borderRadius: 8,
                                    padding: 10,
                                    cursor: "pointer",
                                    backgroundColor: selectedSetId === null ? "#e7f1ff" : "white",
                                    minWidth: 160,
                                    textAlign: "center"
                                }}
                            >
                                <div style={{ fontWeight: "bold", marginBottom: 6 }}>Pick Individually</div>
                                <div style={{ fontSize: 11, color: "#666" }}>Choose one decoration per location below</div>
                            </div>
                            {sets.map((set, idx) => (
                                <div
                                    key={set.setId}
                                    onClick={() => handleSetSelect(set.setId)}
                                    style={{
                                        border: selectedSetId === set.setId ? "3px solid #007bff" : "2px solid #ccc",
                                        borderRadius: 8,
                                        padding: 10,
                                        cursor: "pointer",
                                        backgroundColor: selectedSetId === set.setId ? "#e7f1ff" : "white",
                                        minWidth: 160
                                    }}
                                >
                                    <div style={{ fontWeight: "bold", marginBottom: 6, textAlign: "center" }}>
                                        Set {idx + 1}
                                        <span style={{ fontSize: 11, color: "#666", marginLeft: 6 }}>({set.decorations.length} logos)</span>
                                    </div>
                                    <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center" }}>
                                        {set.decorations.map(renderLogoThumb)}
                                    </div>
                                    {selectedSetId === set.setId && (
                                        <div style={{ marginTop: 5, color: "#007bff", fontWeight: "bold", fontSize: 11, textAlign: "center" }}>
                                            ✓ Selected
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {selectedSetId === null && decorationsByLocation && decorationsByLocation.length === 0 && sets.length > 0 && (
                    <p style={{ color: "#666" }}>No individual decorations available — pick a logo set above.</p>
                )}

                {selectedSetId === null && decorationsByLocation && decorationsByLocation.map((locationGroup, index) => (
                    <div key={locationGroup.locationId} style={{ 
                        marginBottom: 20, 
                        padding: 15, 
                        border: "1px solid #ddd",
                        borderRadius: 5,
                        backgroundColor: "#f9f9f9"
                    }}>
                        <h5 style={{ marginBottom: 15, color: "#333" }}>
                            {locationGroup.locationName}
                            {locationGroup.decorations.length === 1 && (
                                <span style={{ fontSize: 12, color: "#666", marginLeft: 10 }}>(auto-selected)</span>
                            )}
                        </h5>
                        
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                            {locationGroup.decorations.map((decoration, decoIndex) => {
                                const isSelected = selectedDecorations[locationGroup.locationId]?.id === decoration.id;
                                return (
                                    <div 
                                        key={decoration.id}
                                        onClick={() => handleDecorationChange(locationGroup.locationId, decoration)}
                                        style={{
                                            border: isSelected ? "3px solid #007bff" : "2px solid #ccc",
                                            borderRadius: 8,
                                            padding: 10,
                                            cursor: "pointer",
                                            backgroundColor: isSelected ? "#e7f1ff" : "white",
                                            transition: "all 0.2s ease",
                                            minWidth: 150,
                                            textAlign: "center"
                                        }}
                                    >
                                        <img 
                                            src={apiUrl + decoration.decoration_logo.imageUrl} 
                                            height={60} 
                                            style={{ 
                                                padding: 5, 
                                                backgroundColor: "#CCCCCC",
                                                borderRadius: 4,
                                                marginBottom: 8
                                            }} 
                                            alt={decoration.decoration_logo.decorationName}
                                        />
                                        <div style={{ fontSize: 12, fontWeight: "bold" }}>
                                            {decoration.decoration_logo.decorationName}
                                        </div>
                                        <div style={{ fontSize: 11, color: "#666" }}>
                                            {decoration.decoration_logo.decoType}
                                        </div>
                                        {isSelected && (
                                            <div style={{ 
                                                marginTop: 5, 
                                                color: "#007bff", 
                                                fontWeight: "bold",
                                                fontSize: 11
                                            }}>
                                                ✓ Selected
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={close}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleConfirm}>
                    Add to {displayName}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default SelectDecorationsModal;

